import { Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, map, Observable, of } from 'rxjs';

import { CfiRecordEditComponent, FieldOption, formatSsnForDisplay, ssnDigits } from '@k12/app-components';
import { HouseholdStudent, SchoolTermGradesResponse, ToastMessageType, UserRole } from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { errorToastValue, HouseholdService, LoadingSpinnerService, ToastMessageService } from '@k12/app-services';
import { FormatDatePipe } from '@k12/pipes';
import { STUDENT_INFORMATION_SECTIONS, STUDENT_INFORMATION_OPTIONS } from '../student-information.config';
import { getEndorsementDisplay, isEndorsed } from '../student-endorsement.util';
import { resolveEnrollmentStatusId, isAwaitingCertification } from '../student-enrollment-status.util';

type DropdownSchool = {
  name: string;
  id?: string | number;
  schoolId?: string | number;
  schoolTypeId?: string | number | null;
  typeId?: string | number | null;
};

type DropdownSchoolType = {
  name: string;
  id?: string | number;
  schoolTypeId?: string | number;
};

@Component({
  selector: 'cfi-student-information-edit',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, CfiRecordEditComponent],
  template: `
      <cfi-record-edit
        [title]="studentName"
        [sections]="sections"
        [form]="form"
        [options]="options"
        [searchHandlers]="searchHandlers"
        [selectedOptions]="selectedOptions"
        pageTitle="Edit Student Record"
        prefix="student_information"
        [userRole]="userRole"
        [isSaving]="isSaving"
        (save)="onSave()"
        (cancelled)="onCancel()" />
  `
})
export class StudentInformationEditComponent implements OnInit {
  public studentId = '';
  public studentInfo: HouseholdStudent | null = null;
  public userRole: UserRole | null = null;
  public studentName = '';
  public isSaving = false;
  public academicYearId: number | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly householdService = inject(HouseholdService);
  private readonly schoolsService = inject(SchoolsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);

  readonly sections = STUDENT_INFORMATION_SECTIONS;
  readonly options = STUDENT_INFORMATION_OPTIONS;
  form: FormGroup = this.fb.group({});
  private readonly schoolTypeBySchoolId = new Map<string, number | null>();

  /** Seeds the school dropdown's label so it reads correctly before the school list loads. */
  selectedOptions: Record<string, FieldOption | null> = {};

  readonly searchHandlers: Record<string, (query: string) => Observable<FieldOption[]>> = {
    households: (query) =>
      this.householdService.searchGuardianHouseholds(query).pipe(
        map((results) =>
          results.map((r) => ({
            label: `${r.guardianPid ?? ''} — ${r.guardianFullName ?? ''}`,
            value: r.householdId
          }))
        )
      )
  };

  ngOnInit(): void {
    this.studentId = this.route.snapshot.paramMap.get('studentId') ?? '';
    this.academicYearId = this.parseAcademicYearId(this.route.snapshot.queryParamMap.get('academicYearId'));

    const resolved = this.route.parent?.snapshot.data['student'] ?? this.route.snapshot.data['student'];
    this.studentInfo = (resolved?.detail as HouseholdStudent) ?? null;
    this.userRole = (resolved?.role as UserRole) ?? 'partner';

    if (!this.studentInfo) return;

    this.studentName = [this.studentInfo.firstName, this.studentInfo.middleName, this.studentInfo.lastName]
      .filter(Boolean)
      .join(' ');
    this.form = this.buildForm(this.userRole);

    this.options['gradeLevels'] =
      this.studentInfo.gradeId != null && this.studentInfo.grade
        ? [{ label: this.studentInfo.grade, value: this.studentInfo.gradeId }]
        : [];

    this.form.patchValue({
      ...this.studentInfo,
      attendanceTypeId: resolveEnrollmentStatusId(this.studentInfo),
      dob: FormatDatePipe.toDate(this.studentInfo.dob),
      gradeId: this.studentInfo.gradeId || null,
      ssn: formatSsnForDisplay(this.studentInfo.ssn),
      endorsedBySchoolFall: isEndorsed(this.studentInfo.endorsedBySchoolFall),
      endorsedBySchoolSpring: isEndorsed(this.studentInfo.endorsedBySchoolSpring),
      endorsedByParentFallDisplay: getEndorsementDisplay(this.studentInfo.endorsedByParentFall),
      endorsedByParentSpringDisplay: getEndorsementDisplay(this.studentInfo.endorsedByParentSpring)
    });
    // Only seed a label we actually have — an empty one would pin a blank row over the real name.
    this.selectedOptions = {
      schools: this.studentInfo.schoolId != null && this.studentInfo.schoolName
        ? { label: this.studentInfo.schoolName, value: this.studentInfo.schoolId }
        : null
    };
    this.registerSsnHandler();
    this.loadDropdowns();
    this.registerCertificationHandler();
  }

  /**
   * The SSN is shown in full and edited in place, so an admin must leave a complete number behind.
   * Required is applied here rather than in the config because the control is built for every role
   * but only rendered for admins — a required blank would block a household save with no field on
   * screen to fix.
   */
  private registerSsnHandler(): void {
    if (this.userRole !== 'admin') return;

    const control = this.form.get('ssn');
    control?.addValidators(Validators.required);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private buildForm(role: UserRole): FormGroup {
    const editableFields = STUDENT_INFORMATION_SECTIONS
      .flatMap((section) => section.fields)
      .filter((field) => role === 'admin' || field.hideInEdit || !field.editableBy || field.editableBy.includes(role));

    return this.fb.group(
      editableFields.reduce<Record<string, [null, ValidatorFn[]]>>((controls, field) => {
        controls[field.key] = [null, field.hideInEdit ? [] : (field.validators ?? [])];
        return controls;
      }, {})
    );
  }

  private loadDropdowns(): void {
    this.loadingSpinnerService.showSpinner();
    const schoolId = this.form.get('schoolId')?.value;
    const guardianPid = this.studentInfo?.guardianPid;

    forkJoin({
      schools: this.schoolsService.getSchoolsForDropdown('', true),
      schoolTypes: this.schoolsService.getSchoolTypesForDropdown('', false),
      grades: schoolId ? this.schoolsService.getSchoolTermGrades(String(schoolId)) : of(null),
      guardian: this.userRole === 'admin' && guardianPid
        ? this.householdService.searchGuardianHouseholds(guardianPid)
        : of(null)
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingSpinnerService.hideSpinner())
      )
      .subscribe({
        next: ({ schools, schoolTypes, grades, guardian }) => {
          const schoolEntities = (schools.entity ?? []) as DropdownSchool[];
          this.options['schools'] = schoolEntities.map((s) => ({ label: s.name, value: s.schoolId ?? s.id ?? null }));
          this.schoolTypeBySchoolId.clear();
          schoolEntities.forEach((s) => {
            const id = String(s.schoolId ?? s.id ?? '');
            if (!id) return;
            const schoolTypeId = s.schoolTypeId ?? s.typeId ?? null;
            this.schoolTypeBySchoolId.set(id, schoolTypeId == null ? null : Number(schoolTypeId));
          });

          this.options['schoolTypes'] = ((schoolTypes.entity ?? []) as DropdownSchoolType[]).map((t) => ({
            label: t.name,
            value: t.schoolTypeId ?? t.id ?? null
          }));
          this.options['gradeLevels'] = this.toGradeOptions(grades);

          const match = guardian?.find((r) => r.guardianPid === guardianPid) ?? guardian?.[0];
          if (match) {
            const option: FieldOption = {
              label: `${match.guardianPid ?? ''} — ${match.guardianFullName ?? ''}`,
              value: match.householdId
            };
            this.options['households'] = [option];
            this.form.get('guardianPid')?.setValue(option, { emitEvent: false });
          }

          this.registerSchoolSync();
        },
        error: (error) => {
          this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
        }
      });
  }

  private toGradeOptions(res: SchoolTermGradesResponse | null): FieldOption[] {
    const seen = new Set<number>();
    const options: FieldOption[] = [];
    for (const tg of (res?.entity ?? []).flatMap((e) => e.schoolTermGrades ?? [])) {
      if (tg.gradeId == null || seen.has(tg.gradeId)) continue;
      seen.add(tg.gradeId);
      options.push({ label: tg.gradeName || String(tg.gradeId), value: tg.gradeId });
    }
    return options.sort((a, b) => Number(a.value) - Number(b.value));
  }

  private registerSchoolSync(): void {
    const schoolIdControl = this.form.get('schoolId');
    const schoolTypeIdControl = this.form.get('schoolTypeId');
    const gradeControl = this.form.get('gradeId');
    if (!schoolIdControl) return;

    const syncCategory = (schoolId: unknown): void =>
      schoolTypeIdControl?.setValue(schoolId ? this.schoolTypeBySchoolId.get(String(schoolId)) ?? null : null, {
        emitEvent: false
      });

    syncCategory(schoolIdControl.value);

    schoolIdControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((schoolId) => {
      syncCategory(schoolId);
      // Clear the grade on school change so a stale grade can't be saved against a school that doesn't offer it.
      gradeControl?.setValue(null);
      this.options['gradeLevels'] = [];
      if (!schoolId) return;
      this.schoolsService
        .getSchoolTermGrades(String(schoolId))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.options['gradeLevels'] = this.toGradeOptions(res);
        });
    });
  }

  private registerCertificationHandler(): void {
    if (!this.studentInfo) return;

    // A school can endorse a student without certifying them, so the roster status — not the
    // certification date — marks the point where the school stops being changeable here and the
    // household has to go through withdrawal/transfer instead.
    const isPastCertification = !isAwaitingCertification(this.studentInfo);
    const isNotAttending = Number(this.studentInfo.attendanceTypeId) === 3;

    const canChangeSchool = this.userRole === 'admin' || this.userRole === 'household';
    const canEditSchoolDetails = canChangeSchool && (!isPastCertification || isNotAttending);

    ['schoolId', 'gradeId'].forEach((key) => {
      const control = this.form.get(key);
      if (canEditSchoolDetails) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    });

    // Enrollment Status is display-only on this page.
    this.form.get('attendanceTypeId')?.disable({ emitEvent: false });

    if (this.userRole === 'household') {
      this.setHomeschoolFieldsReadonlyForHousehold();
    }
  }

  private setHomeschoolFieldsReadonlyForHousehold(): void {
    const homeschoolFields = [
      'homeschoolIdNumber',
      'homeschoolName',
      'homeschoolAdministratorName',
      'homeschoolCounty'
    ];

    homeschoolFields.forEach((field) => {
      this.form.get(field)?.disable({ emitEvent: false });
    });
  }

  onCancel(): void {
    if (this.academicYearId == null) {
      this.router.navigate(['/student', this.studentId], {
        info: { skipResolve: true, studentData: this.route.parent?.snapshot.data['student'] }
      });
    } else {
      this.router.navigate(['/student', this.studentId], {
        info: { skipResolve: true, studentData: this.route.parent?.snapshot.data['student'] },
        queryParams: { academicYearId: this.academicYearId }
      });
    }
  }

  onSave(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid || !this.studentInfo) return;

    const raw = this.form.getRawValue();

    // School and grade must be set together: both or neither.
    const hasSchool = raw.schoolId != null && raw.schoolId !== '';
    const hasGrade = raw.gradeId != null && raw.gradeId !== '';
    if (hasSchool !== hasGrade) {
      this.toastMessageService.showToast(
        ToastMessageType.Error,
        'Please select both a school and a grade, or leave both empty.');
      return;
    }

    this.isSaving = true;

    const guardianValue: FieldOption | string | null = raw.guardianPid ?? null;
    const newHouseholdId = guardianValue && typeof guardianValue === 'object'
      ? String(guardianValue.value)
      : undefined;
    const schoolTypeId = raw.schoolTypeId ?? this.studentInfo.schoolTypeId;
    const isHomeschool = Number(schoolTypeId) === 3;


    const payload: HouseholdStudent = {
      ...this.studentInfo,
      ...raw,
      guardianPid: this.studentInfo.guardianPid,
      dob: FormatDatePipe.formatDateToString(raw.dob) ?? this.studentInfo.dob,
      ssn: this.normalizeSsn(raw.ssn, this.studentInfo.ssn),
      endorsedByParentFall: this.toBoolean(raw.endorsedByParentFall),
      endorsedByParentSpring: this.toBoolean(raw.endorsedByParentSpring),
      enrollmentConfirmed: this.toBoolean(raw.enrollmentConfirmed),
      hasHighSchoolDiploma: this.toBoolean(raw.hasHighSchoolDiploma),
      enrolledInCollege: this.toBoolean(raw.enrolledInCollege),
      attendanceTypeId: this.studentInfo.attendanceTypeId,
      iepVerified: this.toBoolean(raw.iepVerified),
      homeschoolIdNumber: isHomeschool ? this.normalizeOptionalText(raw.homeschoolIdNumber) : undefined,
      homeschoolName: isHomeschool ? this.normalizeOptionalText(raw.homeschoolName) : undefined,
      homeschoolAdministratorName: isHomeschool ? this.normalizeOptionalText(raw.homeschoolAdministratorName) : undefined,
      homeschoolCounty: isHomeschool ? this.normalizeOptionalText(raw.homeschoolCounty) : undefined,
      newHouseholdId
    };

    // One save: HouseholdApp.UpdateStudentInfo persists student fields, school, grade and homeschool.
    this.householdService
      .saveStudentInfo(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSaving = false)
      )
      .subscribe({
        next: () => {
          this.toastMessageService.showToast(ToastMessageType.Success, 'Student information updated successfully');
          this.householdService
            .getHouseholdStudentDetails(this.studentId, this.academicYearId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((detail) => {
             if(this.academicYearId == null){
                this.router.navigate(['/student', this.studentId], {
                  info: {
                    skipResolve: true,
                    studentData: { ...this.route.parent?.snapshot.data['student'], detail }
                  }
                });
             }
             else
             {
              this.router.navigate(['/student', this.studentId], {
                  info: {
                    skipResolve: true,
                    studentData: { ...this.route.parent?.snapshot.data['student'], detail }
                  },
                  queryParams: {
                    academicYearId: this.academicYearId
                  }
                });
             }
              
            });
        },
        error: (error) => {
          this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
        }
      });
  }

  /** The field holds the grouped SSN; it is sent as digits. Falls back for roles that can't edit it. */
  private normalizeSsn(value: string | number | null | undefined, fallback: string | null | undefined): string {
    return ssnDigits(value) || ssnDigits(fallback);
  }

  private toBoolean(value: unknown): boolean | undefined {
    if (value === 'Yes' || value === true) return true;
    if (value === 'No' || value === false) return false;
    return undefined;
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  private parseAcademicYearId(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

}
